import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Gavel, MessageSquare, User, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAllDebates, DebateWithMeta } from '@/hooks/social/useAllDebates';
import { formatDistanceToNow } from 'date-fns';
import { nb } from 'date-fns/locale';

interface DebateFeedItemProps {
  debate: DebateWithMeta;
  onClick: () => void;
}

const DebateFeedItem: React.FC<DebateFeedItemProps> = ({ debate, onClick }) => {
  const { t } = useTranslation();
  
  const creatorName = debate.creator_profile?.display_name || 'Anonym';
  const timeAgo = formatDistanceToNow(new Date(debate.created_at), { 
    addSuffix: true,
    locale: nb 
  });

  return (
    <Card 
      className="cursor-pointer hover:bg-accent/50 transition-colors"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="space-y-3">
          {/* Header with creator info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Avatar className="h-8 w-8">
                <AvatarImage src={debate.creator_profile?.avatar_url} />
                <AvatarFallback>
                  <User className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <div>
                <span className="text-sm font-medium">{creatorName}</span>
                <span className="text-xs text-muted-foreground ml-2">{timeAgo}</span>
              </div>
            </div>
            <Badge variant="secondary" className="flex items-center gap-1">
              <MessageSquare className="h-3 w-3" />
              {debate.argument_count}
            </Badge>
          </div>

          {/* Assertion */}
          <div className="flex items-start space-x-2">
            <Gavel className="h-4 w-4 mt-1 text-muted-foreground shrink-0" />
            <p className="text-sm font-medium line-clamp-2">{debate.assertion}</p>
          </div>

          {/* Article link */}
          {debate.interview && (
            <div className="flex items-center justify-between pt-2 border-t">
              <span className="text-xs text-muted-foreground truncate max-w-[80%]">
                {debate.interview.title}
              </span>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const DebateFeed: React.FC = () => {
  const { debates, isLoading, error } = useAllDebates(50);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleDebateClick = (debate: DebateWithMeta) => {
    // Navigate to the debates page and scroll to/expand this specific debate
    // The article route doesn't properly show the debate, so we use query params
    if (debate.interview?.id) {
      navigate(`/article/${debate.interview.id}?expandDebate=${debate.id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="animate-pulse space-y-3">
                <div className="flex items-center space-x-2">
                  <div className="h-8 w-8 bg-muted rounded-full" />
                  <div className="h-4 w-24 bg-muted rounded" />
                </div>
                <div className="h-4 w-full bg-muted rounded" />
                <div className="h-4 w-3/4 bg-muted rounded" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          {t('debates.errorLoading')}
        </CardContent>
      </Card>
    );
  }

  if (debates.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Gavel className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <p className="text-muted-foreground">{t('debates.noActiveDebates')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {debates.map((debate) => (
        <DebateFeedItem 
          key={debate.id} 
          debate={debate} 
          onClick={() => handleDebateClick(debate)}
        />
      ))}
    </div>
  );
};
